import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DevMenuModel } from '../dk-side-navigation-menu/navigation';

/**
 * Açılır menü (16.10.2026, deneme): sol menü daraltılmışken (45px ikon şeridi) paneli açmadan
 * eski Windows usulü basamaklı menü — şeritteki modül ikonuna tıklayınca içeriği sağa açılır,
 * klasörün üstüne gelince alt seviye bir sağına açılır; forma tıklayınca sekmede açılır.
 *
 * GERİ ALMAK İÇİN: ACILIR_MENU_AKTIF = false yeterli — daraltılmış şerit eski hâline döner
 * (şeride tıklayınca ağaç panel geçici açılır). Bu klasör ve dk-side-navigation-menu'deki
 * `acilirMenu` dalları silinebilir.
 */
export const ACILIR_MENU_AKTIF = true;

/** Bir açılır pencere (seviye). tur: 'menu' modül/klasör içeriği, 'ara' form arama, 'fav' favoriler. */
interface AcilirPencere {
  tur: 'menu' | 'ara' | 'fav';
  baslik: string;
  ikon?: string;
  renk?: string;
  ogeler: DevMenuModel[];
  /** Bu pencerede alt penceresi açık olan öğe (satır vurgusu). */
  acikOge?: DevMenuModel;
  top: number;
  left: number;
  maxHeight: number;
}

/** Arama sonucu: form + ana dalların adı (nerede olduğunu göstermek için). */
interface AramaSonucu {
  oge: DevMenuModel;
  yol: string;
  renk?: string;
}

const PENCERE_GENISLIK = 264;
const SATIR_YUKSEKLIK = 28;
const BASLIK_YUKSEKLIK = 36;
const KENAR = 8;
/** Klasörün üstünde bu kadar durunca alt seviye açılır (çapraz geçişte titremesin). */
const ALT_ACMA_GECIKME = 160;
/** Adlı şerit (17.10.2026): fare şeritte bu kadar durunca şerit modül adlarıyla genişler (geçerken açılmasın). */
const GENISLEME_GECIKME = 200;
/** Adlı şeritten çıkınca bu kadar beklenir (çapraz geçişte kapanıp açılmasın). */
const DARALMA_GECIKME = 300;
/** Adlı şeridin genişliği (px). */
const GENIS_SERIT = 248;
/** Pencere açıkken başka modülün üstünde bu kadar durunca ona geçilir; üstünden geçip gidince geçilmez. */
const KOK_GECIS_GECIKME = 220;

@Component({
  selector: 'dk-acilir-menu',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dk-acilir-menu.component.html',
  styleUrls: ['./dk-acilir-menu.component.scss'],
})
export class DkAcilirMenuComponent implements AfterViewInit, OnDestroy {
  /** Kök seviye (Tüm Uygulamalar'da modüller, modül seçiliyse o modülün dalları). */
  @Input() menu: DevMenuModel[] = [];
  // eski hâli (17.10.2026): @Input() favoriler: DevMenuModel[] = [];
  /** Favori formlar; değişince açık "Favorilerim" penceresi ve satırlardaki yıldızlar tazelenir. */
  @Input() set favoriler(liste: DevMenuModel[]) {
    this._favoriler = liste ?? [];
    this.favKodlari = new Set(this._favoriler.map((f) => f?.formId).filter((k): k is number => !!k));
    const acikFav = this.pencereler.find((p) => p.tur === 'fav');
    if (acikFav) acikFav.ogeler = this._favoriler;
  }
  get favoriler(): DevMenuModel[] {
    return this._favoriler;
  }
  private _favoriler: DevMenuModel[] = [];
  private favKodlari = new Set<number>();
  /** 17.10.2026: daraltılmış menüde favori ekle/çıkar (ağaçtaki sağ tık menüsünün karşılığı). */
  @Output() favoriDegistir = new EventEmitter<{ oge: DevMenuModel; ekle: boolean }>();
  /** Forma tıklandı: sekmede açılsın. */
  @Output() formSec = new EventEmitter<DevMenuModel>();
  /** Şeridin altındaki düğme: ağaç menüyü (geniş paneli) aç. */
  @Output() genislet = new EventEmitter<void>();

  @ViewChild('katman') katman?: ElementRef<HTMLElement>;
  @ViewChild('aramaKutusu') aramaKutusu?: ElementRef<HTMLInputElement>;

  pencereler: AcilirPencere[] = [];
  /** Şeritte penceresi açık olan düğme (vurgu için). */
  acikKok: DevMenuModel | 'ara' | 'fav' | null = null;

  aramaMetni = '';
  aramaSonuclari: AramaSonucu[] = [];
  aramaSecili = 0;

  private altZamanlayici?: ReturnType<typeof setTimeout>;
  private kokZamanlayici?: ReturnType<typeof setTimeout>;

  /**
   * Adlı şerit (17.10.2026): ikonlar ezber istiyordu. Fare şeride girince şerit içeriğin üstüne binerek
   * modül adlarıyla genişler (body katmanında, aynı hizada); fare çıkınca ve pencere açık değilse daralır.
   */
  genis = false;
  genisKonum = { top: 0, left: 0, height: 0 };
  readonly genisGenislik = GENIS_SERIT;
  private fareGeniste = false;
  private genisZamanlayici?: ReturnType<typeof setTimeout>;
  private daraltZamanlayici?: ReturnType<typeof setTimeout>;
  @ViewChild('seritListe') seritListe?: ElementRef<HTMLElement>;
  @ViewChild('genisListe') genisListe?: ElementRef<HTMLElement>;

  constructor(private host: ElementRef<HTMLElement>, private cdr: ChangeDetectorRef) {}

  ngAfterViewInit() {
    // Pencereler body'ye taşınır: drawer paneli overflow:hidden / transform ile kesmesin.
    const k = this.katman?.nativeElement;
    if (k) document.body.appendChild(k);
  }

  ngOnDestroy() {
    clearTimeout(this.altZamanlayici);
    clearTimeout(this.genisZamanlayici);
    clearTimeout(this.daraltZamanlayici);
    this.katman?.nativeElement.remove();
  }

  // ---------------------------------------------------------------- şerit

  ikonSinifi(oge: DevMenuModel | undefined): string {
    const ikon = oge?.icon ?? '';
    if (!ikon) return 'fa-solid fa-folder';
    return ikon.includes('fa-') ? ikon : `dx-icon dx-icon-${ikon}`;
  }

  altVar(oge: DevMenuModel): boolean {
    return !!oge?.items?.length;
  }

  /** Favoriye eklenebilen: form kimliği olan yaprak (klasör/modül değil). */
  favoriOlabilir(oge: DevMenuModel | undefined): boolean {
    return !!oge?.formId && !this.altVar(oge);
  }

  favoriMi(oge: DevMenuModel | undefined): boolean {
    return !!oge?.formId && this.favKodlari.has(oge.formId);
  }

  /** Satırdaki yıldız ya da sağ tık: formu açmadan favoriye ekler / çıkarır. */
  favoriTikla(oge: DevMenuModel, olay: Event) {
    olay.stopPropagation();
    olay.preventDefault();
    if (!this.favoriOlabilir(oge)) return;
    this.favoriDegistir.emit({ oge, ekle: !this.favoriMi(oge) });
  }

  kokTikla(oge: DevMenuModel, olay: Event) {
    olay.stopPropagation();
    if (!this.altVar(oge)) {
      if (oge.path) this.formAc(oge);
      return;
    }
    if (this.acikKok === oge) {
      this.kapat();
      return;
    }
    this.kokAc(oge, olay.currentTarget as HTMLElement);
  }

  // eski hâli (17.10.2026): fare açık pencereye çapraz giderken yol üstündeki modüle geçiyordu
  // /** Bir pencere açıkken başka şerit ikonunun üstüne gelince ona geçilir (menü çubuğu davranışı). */
  // kokUzerinde(oge: DevMenuModel, olay: Event) {
  //   if (!this.pencereler.length || this.acikKok === oge || !this.altVar(oge)) return;
  //   this.kokAc(oge, olay.currentTarget as HTMLElement);
  // }
  /**
   * Bir pencere açıkken başka şerit ikonunun üstünde KOK_GECIS_GECIKME kadar durunca ona geçilir (menü çubuğu
   * davranışı). Fare açık pencereye çapraz giderken üstünden geçtiği modüller geçişi tetiklemez (kokCikti iptal).
   */
  kokUzerinde(oge: DevMenuModel, olay: Event) {
    clearTimeout(this.kokZamanlayici);
    if (!this.pencereler.length || this.acikKok === oge || !this.altVar(oge)) return;
    const dugme = olay.currentTarget as HTMLElement;
    this.kokZamanlayici = setTimeout(() => {
      if (!this.pencereler.length) return;
      this.kokAc(oge, dugme);
      this.cdr.markForCheck();
    }, KOK_GECIS_GECIKME);
  }

  kokCikti() {
    clearTimeout(this.kokZamanlayici);
  }

  // eski hâli (17.10.2026, kök pencereler adlı şeridin sağına hizalanır):
  // private kokAc(oge: DevMenuModel, dugme: HTMLElement) {
  //   this.acikKok = oge;
  //   this.pencereler = [this.pencereKur('menu', oge.text ?? '', oge.items ?? [], dugme.getBoundingClientRect(), null, oge)];
  // }
  private kokAc(oge: DevMenuModel, dugme: HTMLElement) {
    this.acikKok = oge;
    this.pencereler = [this.pencereKur('menu', oge.text ?? '', oge.items ?? [], this.kokHiza(dugme), null, oge)];
  }

  /**
   * Kök pencerenin hizası: düğmenin satırı, sağ kenar adlı şeridin sağı. Dar şeritten tıklanırsa (dokunmatik,
   * gecikme dolmadan) şerit önce genişler; pencere adların üstüne binmesin.
   */
  private kokHiza(dugme: HTMLElement): DOMRect {
    this.genisAc();
    const r = dugme.getBoundingClientRect();
    const sag = this.genisKonum.left + GENIS_SERIT;
    return new DOMRect(r.left, r.top, sag - r.left, r.height);
  }

  // ---------------------------------------------------------------- adlı şerit

  seritGirdi() {
    clearTimeout(this.daraltZamanlayici);
    if (this.genis) return;
    clearTimeout(this.genisZamanlayici);
    this.genisZamanlayici = setTimeout(() => {
      this.genisAc();
      this.cdr.markForCheck();
    }, GENISLEME_GECIKME);
  }

  seritCikti() {
    clearTimeout(this.genisZamanlayici);
  }

  private genisAc() {
    clearTimeout(this.genisZamanlayici);
    clearTimeout(this.daraltZamanlayici);
    if (this.genis) return;
    const serit = this.host.nativeElement.querySelector('.serit') as HTMLElement | null;
    const r = serit?.getBoundingClientRect();
    if (!r) return;
    this.genisKonum = { top: r.top, left: r.left, height: r.height };
    this.genis = true;
    this.fareGeniste = true;
    // modül listesi kaydırılmışsa adlı şerit aynı yerden başlasın (düğmeler üst üste gelsin)
    const kaydirma = this.seritListe?.nativeElement.scrollTop ?? 0;
    setTimeout(() => {
      if (this.genisListe) this.genisListe.nativeElement.scrollTop = kaydirma;
    });
  }

  genisGirdi() {
    this.fareGeniste = true;
    clearTimeout(this.daraltZamanlayici);
  }

  genisCikti() {
    this.fareGeniste = false;
    this.daraltPlanla();
  }

  /** Pencere açıksa adlı şerit açık kalır (pencere kapanınca kapat() daraltır). */
  private daraltPlanla() {
    clearTimeout(this.daraltZamanlayici);
    this.daraltZamanlayici = setTimeout(() => {
      if (!this.pencereler.length && !this.fareGeniste) {
        this.genis = false;
        this.cdr.markForCheck();
      }
    }, DARALMA_GECIKME);
  }

  araAc(olay: Event) {
    olay.stopPropagation();
    if (this.acikKok === 'ara') return this.kapat();
    this.acikKok = 'ara';
    // eski hâli (17.10.2026): const r = (olay.currentTarget as HTMLElement).getBoundingClientRect();
    const r = this.kokHiza(olay.currentTarget as HTMLElement);
    const p = this.pencereKur('ara', 'Form Arama', [], r, null);
    p.maxHeight = window.innerHeight - p.top - KENAR;
    this.pencereler = [p];
    this.aramaMetni = '';
    this.aramaSonuclari = [];
    this.aramaSecili = 0;
    setTimeout(() => this.aramaKutusu?.nativeElement.focus());
  }

  favAc(olay: Event) {
    olay.stopPropagation();
    if (this.acikKok === 'fav') return this.kapat();
    this.acikKok = 'fav';
    // eski hâli (17.10.2026): const r = (olay.currentTarget as HTMLElement).getBoundingClientRect();
    const r = this.kokHiza(olay.currentTarget as HTMLElement);
    this.pencereler = [this.pencereKur('fav', 'Favorilerim', this.favoriler ?? [], r, null)];
  }

  // ---------------------------------------------------------------- pencereler

  /** Pencereyi tetikleyen satır/düğmenin hizasına koyar; ekrana sığmıyorsa yukarı / sola kaydırır. */
  private pencereKur(
    tur: AcilirPencere['tur'],
    baslik: string,
    ogeler: DevMenuModel[],
    hiza: DOMRect,
    ust: DOMRect | null,
    kaynak?: DevMenuModel
  ): AcilirPencere {
    const tahmini = BASLIK_YUKSEKLIK + Math.max(ogeler.length, 1) * SATIR_YUKSEKLIK + 10;
    const yukseklik = Math.min(tahmini, window.innerHeight - 2 * KENAR);
    const top = Math.max(KENAR, Math.min(hiza.top - (ust ? BASLIK_YUKSEKLIK : 0), window.innerHeight - yukseklik - KENAR));
    let left = (ust ?? hiza).right + (ust ? -2 : 6);
    if (left + PENCERE_GENISLIK > window.innerWidth - KENAR) {
      // sağa sığmıyor: bir önceki pencerenin soluna aç
      left = Math.max(KENAR, (ust ? ust.left : hiza.left) - PENCERE_GENISLIK + 2);
    }
    return {
      tur,
      baslik,
      ikon: kaynak ? this.ikonSinifi(kaynak) : undefined,
      renk: kaynak?.renk,
      ogeler,
      top,
      left,
      maxHeight: window.innerHeight - top - KENAR,
    };
  }

  /** 17.10.2026: satırın üstünden geçip gidince (çapraz geçiş) bekleyen alt pencere açılmaz. */
  satirCikti() {
    clearTimeout(this.altZamanlayici);
  }

  satirUzerinde(seviye: number, oge: DevMenuModel, olay: Event) {
    clearTimeout(this.altZamanlayici);
    const satir = olay.currentTarget as HTMLElement;
    this.altZamanlayici = setTimeout(() => {
      this.altAc(seviye, oge, satir);
      this.cdr.markForCheck();
    }, ALT_ACMA_GECIKME);
  }

  satirTikla(seviye: number, oge: DevMenuModel, olay: Event) {
    olay.stopPropagation();
    clearTimeout(this.altZamanlayici);
    if (this.altVar(oge)) {
      // dokunmatikte üstüne gelme yok: tıklama alt seviyeyi hemen açar
      this.altAc(seviye, oge, olay.currentTarget as HTMLElement);
    } else if (oge.path) {
      this.formAc(oge);
    }
  }

  /** seviye'deki pencerede `oge` için alt pencere açar; daha derindekileri kapatır. Form ise yalnız derinleri kapatır. */
  private altAc(seviye: number, oge: DevMenuModel, satir: HTMLElement) {
    const pencere = this.pencereler[seviye];
    if (!pencere) return;
    if (pencere.acikOge === oge && this.pencereler.length > seviye + 1) return;
    this.pencereler = this.pencereler.slice(0, seviye + 1);
    pencere.acikOge = undefined;
    if (!this.altVar(oge)) return;
    const ustPencere = satir.closest('.acilir-pencere') as HTMLElement | null;
    pencere.acikOge = oge;
    this.pencereler.push(
      this.pencereKur('menu', oge.text ?? '', oge.items ?? [], satir.getBoundingClientRect(), ustPencere?.getBoundingClientRect() ?? null, oge)
    );
  }

  private formAc(oge: DevMenuModel) {
    this.kapat();
    this.formSec.emit(oge);
  }

  kapat() {
    clearTimeout(this.altZamanlayici);
    clearTimeout(this.kokZamanlayici);
    this.pencereler = [];
    this.acikKok = null;
    // adlı şerit: fare üstündeyse açık kalır, değilse daralır
    if (!this.fareGeniste) this.genis = false;
  }

  // ---------------------------------------------------------------- arama

  aramaYaz(metin: string) {
    this.aramaMetni = metin;
    const aranan = metin.trim().toLocaleLowerCase('tr');
    this.aramaSecili = 0;
    if (aranan.length < 2) {
      this.aramaSonuclari = [];
      return;
    }
    const sonuc: AramaSonucu[] = [];
    const gez = (ogeler: DevMenuModel[], yol: string[], renk?: string) => {
      for (const o of ogeler ?? []) {
        if (sonuc.length >= 60) return;
        if (this.altVar(o)) {
          gez(o.items!, [...yol, o.text ?? ''], o.renk ?? renk);
        } else if (o.path && o.text?.toLocaleLowerCase('tr').includes(aranan)) {
          sonuc.push({ oge: o, yol: yol.join(' › '), renk: o.renk ?? renk });
        }
      }
    };
    gez(this.menu, []);
    this.aramaSonuclari = sonuc;
  }

  aramaTus(olay: KeyboardEvent) {
    const n = this.aramaSonuclari.length;
    if (olay.key === 'ArrowDown' && n) {
      this.aramaSecili = (this.aramaSecili + 1) % n;
      olay.preventDefault();
    } else if (olay.key === 'ArrowUp' && n) {
      this.aramaSecili = (this.aramaSecili - 1 + n) % n;
      olay.preventDefault();
    } else if (olay.key === 'Enter' && n) {
      this.formAc(this.aramaSonuclari[this.aramaSecili].oge);
    }
  }

  aramaSonucTikla(s: AramaSonucu, olay: Event) {
    olay.stopPropagation();
    this.formAc(s.oge);
  }

  // ---------------------------------------------------------------- kapanma

  @HostListener('document:keydown.escape')
  escape() {
    if (this.pencereler.length) this.kapat();
  }

  /** Şerit ve pencereler dışında bir yere basılınca kapanır (basılan yer kendi işini yapar). */
  @HostListener('document:mousedown', ['$event'])
  disariBasildi(olay: MouseEvent) {
    if (!this.pencereler.length) return;
    const hedef = olay.target as Node | null;
    if (!hedef) return;
    if (this.host.nativeElement.contains(hedef) || this.katman?.nativeElement.contains(hedef)) return;
    this.fareGeniste = false;
    this.kapat();
  }

  @HostListener('window:resize')
  boyutDegisti() {
    // eski hâli (17.10.2026): if (this.pencereler.length) this.kapat();
    if (this.pencereler.length || this.genis) {
      this.fareGeniste = false;
      this.kapat();
    }
  }

  izle(_: number, o: DevMenuModel) {
    return o;
  }
}
